import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Loading from "../../components/spinner/Loading";
import { useFormik } from "formik";
import * as Yup from "yup";
import { MdOutlineEdit } from "react-icons/md";
import {
  getBillingDetails,
  updateUserBillingDetails,
} from "./services/myprofile.services";
import { debounce } from "lodash";
import axios from "axios";
import { VscVerifiedFilled } from "react-icons/vsc";
import { getLocalItem } from "../../_helpers/storageUtils";
import { handleUppercaseChange, maskName } from "../../_helpers/formatUtils";
import { API_URL } from "../../app/config";
import { ErrorToast } from "../../toast/Toasts";
import moment from "moment";
import { Country, State, City } from "country-state-city";

const BillingDetails = () => {
  const dispatch = useAppDispatch();
  const billingData = useAppSelector((state) => state.profile.billingData);

  const [maskedName, setMaskedName] = useState(
    maskName(billingData?.billing_pan_card_name || "")
  );
  const [panStatus, setPanStatus] = useState<
    "idle" | "valid" | "invalid" | "loading"
  >("idle");

  const [lastVerifiedPan, setLastVerifiedPan] = useState<string | null>(null);

  const billingLoading = useAppSelector(
    (state) => state.profile.billingLoading
  );
  const StateList = useAppSelector((state) => state.state.StateList);
  const CityList = useAppSelector((state) => state.city.CityList);
  const closeBtnRef = useRef<any>(null);
  const panRef = useRef<any>(null);

  const validationSchema = Yup.object({
    billing_pan_card_no: Yup.string()
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format")
      .required("PAN is required"),
    billing_pincode: Yup.string()
      .matches(/^[1-9][0-9]{5}$/, "Invalid pincode")
      .nullable(),
    billing_gst_no: Yup.string()
      .matches(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        "Invalid GST number"
      )
      .nullable(),
    billing_gst_date: Yup.date()
      .nullable()
      .when("billing_gst_no", {
        is: (gstNo: string | null) => gstNo && gstNo.trim() !== "",
        then: (schema) => schema.required("GST registration date is required"),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  useEffect(() => {
    dispatch(getBillingDetails({ user_guid: getLocalItem("user_guid") }));
  }, []);

  const formik = useFormik({
    initialValues: {
      billing_address: billingData?.billing_address || "",
      billing_state_code: billingData?.billing_state_code || "",
      billing_country_code: billingData?.billing_country_code || "",
      billing_city_name: billingData?.billing_city_name || "",
      billing_state_name: billingData?.billing_state_name || "",
      billing_country_name: billingData?.billing_country_name || "",
      billing_pan_card_no: billingData?.billing_pan_card_no || "",
      billing_pan_card_name: billingData?.billing_pan_card_name || "",
      billing_gst_reg_name: billingData?.billing_gst_reg_name || "",
      billing_gst_date: billingData?.billing_gst_date || "",
      billing_pincode: billingData?.billing_pincode || "",
      billing_gst_no: billingData?.billing_gst_no || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!values?.billing_pan_card_name || panStatus === "loading") {
        ErrorToast("Please enter a valid PAN number and verify it to proceed");
        return;
      }
      const payload = {
        billing_state_code: values?.billing_state_code
          ? values?.billing_state_code
          : "",
        billing_country_code: values?.billing_country_code
          ? values?.billing_country_code
          : "",
        billing_city_name: values?.billing_city_name
          ? values?.billing_city_name
          : "",
        billing_state_name: values?.billing_state_name
          ? values?.billing_state_name
          : "",
        billing_country_name: values?.billing_country_name
          ? values?.billing_country_name
          : "",
        billing_address: values?.billing_address ? values?.billing_address : "",
        billing_pan_card_no: values?.billing_pan_card_no
          ? values?.billing_pan_card_no
          : "",
        billing_pan_card_name: values?.billing_pan_card_name
          ? values?.billing_pan_card_name
          : "",
        billing_gst_reg_name: values?.billing_gst_reg_name
          ? values?.billing_gst_reg_name
          : "",
        billing_gst_date: values?.billing_gst_date
          ? moment(values?.billing_gst_date).format("YYYY-MM-DD")
          : null,
        billing_pincode: values?.billing_pincode ? values?.billing_pincode : "",
        billing_gst_no: values?.billing_gst_no ? values?.billing_gst_no : "",
        user_guid: getLocalItem("user_guid"),
        isactive: true,
      };
      const response = await dispatch(updateUserBillingDetails(payload));
      if (response.meta.requestStatus === "fulfilled") {
        setSubmitting(false);
        resetForm();
        if (closeBtnRef.current) {
          closeBtnRef.current.click();
        }
        dispatch(getBillingDetails({ user_guid: getLocalItem("user_guid") }));
      }
    },
    enableReinitialize: true,
  });

  const fetchPanDetails = debounce(async (pan: string) => {
    setPanStatus("loading");
    try {
      const res = await axios.post(`${API_URL}/pan-verification`, {
        PanNumber: pan,
      });

      if (res.status === 200 && res.data.status === 0 && res.data?.data) {
        const name = `${res.data?.data?.first_name} ${res.data?.data?.middle_name} ${res.data?.data?.last_name}`;

        setMaskedName(maskName(name || ""));
        setPanStatus("valid");
        setLastVerifiedPan(pan);
        formik.setFieldValue("billing_pan_card_name", name);
      } else {
        setPanStatus("invalid");
        setLastVerifiedPan(null);
        formik.setFieldValue("billing_pan_card_name", "");
      }
    } catch (err) {
      setPanStatus("invalid");
      setLastVerifiedPan(null);
      formik.setFieldValue("billing_pan_card_name", "");
    }
  }, 500);

  useEffect(() => {
    const pan = formik.values.billing_pan_card_no?.toUpperCase() || "";

    if (/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) {
      const shouldVerify =
        pan !== lastVerifiedPan && pan !== billingData?.billing_pan_card_no;

      if (shouldVerify) {
        fetchPanDetails(pan);
      } else if (
        billingData?.billing_pan_card_no &&
        billingData?.billing_pan_card_name
      ) {
        setPanStatus("valid");
        setMaskedName(maskName(billingData?.billing_pan_card_name || ""));
        formik.setFieldValue(
          "billing_pan_card_name",
          billingData?.billing_pan_card_name
        );
      }
    } else {
      setPanStatus("idle");
    }
  }, [formik.values.billing_pan_card_no]);

  return (
    <div className="position-relative">
      <Loading loading={billingLoading} />
      <div className="profile-heading">
        <div className="prfile-name">
          <h2>Billing Information</h2>
          <p>
            As per government directive Billing address is compulsory for all
            bookings.
          </p>
        </div>
        <form className="add-travel">
          <a
            className="travel-btn"
            data-bs-toggle="modal"
            data-bs-target="#billingform"
            href="javascript:void(0)"
          >
            <MdOutlineEdit /> Edit
          </a>
          <div
            className="modal fade"
            id="billingform"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex={-1}
            aria-labelledby="edit-profile"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title fs-5" id="edit-profile">
                    Edit Billing Details
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={() => formik.resetForm()}
                  ></button>
                </div>
                <div className="modal-body travel-form-detail">
                  <div className="travel-form">
                    <form>
                      <div className="field-set">
                        <div className="field">
                          <label
                            className={
                              formik.touched.billing_gst_no &&
                              formik.errors.billing_gst_no
                                ? "error-label"
                                : ""
                            }
                          >
                            GST No
                          </label>
                          <input
                            type="text"
                            name="billing_gst_no"
                            value={formik.values.billing_gst_no}
                            onChange={(e) => handleUppercaseChange(formik, e)}
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.billing_gst_no &&
                              formik.errors.billing_gst_no
                                ? "error-border"
                                : ""
                            }
                          />
                          {formik.touched.billing_gst_no &&
                          formik.errors.billing_gst_no &&
                          typeof formik.errors.billing_gst_no === "string" ? (
                            <div className="error-label">
                              {formik.errors.billing_gst_no}
                            </div>
                          ) : null}
                        </div>
                        <div className="field">
                          <label
                          // className={
                          //   formik.touched.billing_gst_reg_name &&
                          //   formik.errors.billing_gst_reg_name
                          //     ? "error-label"
                          //     : ""
                          // }
                          >
                            Registered Name
                          </label>
                          <input
                            type="text"
                            name="billing_gst_reg_name"
                            value={formik.values.billing_gst_reg_name}
                            onChange={(e) => handleUppercaseChange(formik, e)}
                            onBlur={formik.handleBlur}
                            // className={
                            //   formik.touched.billing_gst_reg_name &&
                            //   formik.errors.billing_gst_reg_name
                            //     ? "error-border"
                            //     : ""
                            // }
                          />
                          {/* {formik.touched.billing_gst_reg_name &&
                          formik.errors.billing_gst_reg_name &&
                          typeof formik.errors.billing_gst_reg_name ===
                            "string" ? (
                            <div className="error-label">
                              {formik.errors.billing_gst_reg_name}
                            </div>
                          ) : null} */}
                        </div>
                      </div>
                      <div className="field-set">
                        <div className="field">
                          <label
                            className={
                              formik.touched.billing_gst_date &&
                              formik.errors.billing_gst_date
                                ? "error-label"
                                : ""
                            }
                          >
                            GST Reg. Date
                          </label>
                          <input
                            type="date"
                            name="billing_gst_date"
                            max={moment().format("YYYY-MM-DD")}
                            value={formik.values.billing_gst_date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.billing_gst_date &&
                              formik.errors.billing_gst_date
                                ? "error-border"
                                : ""
                            }
                          />
                          {formik.touched.billing_gst_date &&
                          formik.errors.billing_gst_date &&
                          typeof formik.errors.billing_gst_date === "string" ? (
                            <div className="error-label">
                              {formik.errors.billing_gst_date}
                            </div>
                          ) : null}
                        </div>
                        <div className="field">
                          <label
                          // className={
                          //   formik.touched.billing_address &&
                          //   formik.errors.billing_address
                          //     ? "error-label"
                          //     : ""
                          // }
                          >
                            Address
                          </label>
                          <input
                            type="text"
                            name="billing_address"
                            value={formik.values.billing_address}
                            onChange={(e) => handleUppercaseChange(formik, e)}
                            onBlur={formik.handleBlur}
                            // className={
                            //   formik.touched.billing_address &&
                            //   formik.errors.billing_address
                            //     ? "error-border"
                            //     : ""
                            // }
                          />
                          {/* {formik.touched.billing_address &&
                          formik.errors.billing_address &&
                          typeof formik.errors.billing_address === "string" ? (
                            <div className="error-label">
                              {formik.errors.billing_address}
                            </div>
                          ) : null} */}
                        </div>
                      </div>
                      <div className="field-set">
                        <div className="field">
                          <label>Country</label>
                          <select
                            name="billing_country_code"
                            value={formik.values.billing_country_code}
                            onChange={(e) => {
                              const code = e?.target?.value;
                              const selectedCountry =
                                Country.getAllCountries().find(
                                  (country) => country?.isoCode === code
                                );
                              const name = selectedCountry
                                ? selectedCountry?.name
                                : "";
                              formik.setFieldValue(
                                "billing_country_code",
                                code
                              );
                              formik.setFieldValue(
                                "billing_country_name",
                                name
                              );
                            }}
                            // onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select Country</option>
                            {Country.getAllCountries()?.map((e: any) => (
                              <option key={e?.isoCode} value={e?.isoCode}>
                                {e?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="field">
                          <label>State</label>
                          <select
                            name="billing_state_code"
                            value={formik.values.billing_state_code}
                            onChange={(e) => {
                              const code = e?.target?.value;
                              const selectedState = State.getStatesOfCountry(
                                formik.values.billing_country_code
                              ).find((state) => state?.isoCode === code);
                              const name = selectedState
                                ? selectedState?.name
                                : "";
                              formik.setFieldValue("billing_state_code", code);
                              formik.setFieldValue("billing_state_name", name);
                            }}
                            // onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select State</option>
                            {State?.getStatesOfCountry(
                              formik.values.billing_country_code
                            )?.map((e: any) => (
                              <option key={e?.isoCode} value={e?.isoCode}>
                                {e?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="field-set">
                        <div className="field">
                          <label>City</label>
                          <select
                            name="billing_city_name"
                            value={formik.values.billing_city_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select City</option>
                            {City.getCitiesOfState(
                              formik.values.billing_country_code,
                              formik.values.billing_state_code
                            )?.map((e) => (
                              <option key={e?.name} value={e?.name}>
                                {e?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="field">
                          <label
                            className={
                              formik.touched.billing_pincode &&
                              formik.errors.billing_pincode
                                ? "error-label"
                                : ""
                            }
                          >
                            Pincode
                          </label>
                          <input
                            type="text"
                            name="billing_pincode"
                            value={formik.values.billing_pincode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.billing_pincode &&
                              formik.errors.billing_pincode
                                ? "error-border"
                                : ""
                            }
                          />
                          {formik.touched.billing_pincode &&
                          formik.errors.billing_pincode &&
                          typeof formik.errors.billing_pincode === "string" ? (
                            <div className="error-label">
                              {formik.errors.billing_pincode}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="field-set gap-0 flex-column">
                        <label
                          className={
                            formik.touched.billing_pan_card_no &&
                            formik.errors.billing_pan_card_no
                              ? "error-label"
                              : ""
                          }
                        >
                          PAN No <code>*</code>
                        </label>
                        <div
                          className={
                            formik.touched.billing_pan_card_no &&
                            formik.errors.billing_pan_card_no
                              ? "field w-100 pancard-field error-border"
                              : "field w-100 pancard-field"
                          }
                          onClick={() => {
                            panRef.current?.focus?.();
                          }}
                          style={{ padding: "8px 13px 8px 3px" }}
                        >
                          <input
                            type="text"
                            name="billing_pan_card_no"
                            ref={panRef}
                            value={formik.values.billing_pan_card_no?.toUpperCase()}
                            onChange={(e) => {
                              const val = e.target.value
                                .toUpperCase()
                                .slice(0, 10);
                              formik.setFieldValue("billing_pan_card_no", val);

                              if (val !== lastVerifiedPan) {
                                setPanStatus("idle");
                              }
                            }}
                            placeholder="Enter PAN No here"
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.billing_pan_card_no &&
                              formik.errors.billing_pan_card_no
                                ? "error-border border-0"
                                : "border-0"
                            }
                          />

                          {panStatus === "loading" && (
                            <div className="status">Verifying...</div>
                          )}

                          {panStatus !== "loading" && (
                            <>
                              {formik.touched.billing_pan_card_no &&
                                formik.errors.billing_pan_card_no &&
                                typeof formik.errors.billing_pan_card_no ===
                                  "string" && (
                                  <div className="status error-label">
                                    {formik.errors.billing_pan_card_no}
                                  </div>
                                )}

                              {panStatus === "invalid" &&
                                !formik.errors.billing_pan_card_no && (
                                  <div className="status error-label">
                                    Invalid PAN!
                                  </div>
                                )}
                            </>
                          )}

                          {panStatus === "valid" &&
                            !formik.errors.billing_pan_card_no && (
                              <div className="status success">
                                {maskedName}{" "}
                                <VscVerifiedFilled
                                  style={{ fontSize: 16, color: "#00a34d" }}
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    </form>
                  </div>
                  <div className="travel-detail-btns mt-2">
                    <a
                      className="disable"
                      href="javascript:void(0)"
                      data-bs-dismiss="modal"
                      onClick={() => formik.resetForm()}
                      ref={closeBtnRef}
                    >
                      Cancel
                    </a>
                    <a
                      href="javascript:void(0)"
                      onClick={(event) => {
                        event.preventDefault();
                        formik.handleSubmit();
                      }}
                    >
                      Save
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="travel-table">
        <table>
          <tbody>
            <tr>
              <td className="w-40">GST No</td>
              <td>
                {billingData?.billing_gst_no ? (
                  <span>{billingData?.billing_gst_no}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>Pancard No</td>
              <td>
                {billingData?.billing_pan_card_no ? (
                  <span>{billingData?.billing_pan_card_no}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>Registered Name</td>
              <td>
                {billingData?.billing_gst_reg_name ? (
                  <span>{billingData?.billing_gst_reg_name}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>GST Reg. Date</td>
              <td>
                {billingData?.billing_gst_date ? (
                  <span>
                    {moment(
                      billingData?.billing_gst_date,
                      "YYYY-MM-DD"
                    )?.format("DD/MM/YYYY")}
                  </span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>Address</td>
              <td>
                {billingData?.billing_address ? (
                  <span>{billingData?.billing_address}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>Country</td>
              <td>
                {billingData?.billing_country_name ? (
                  <span>{billingData?.billing_country_name}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>State</td>
              <td>
                {billingData?.billing_state_name ? (
                  <span>{billingData?.billing_state_name}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>City</td>
              <td>
                {billingData?.billing_city_name ? (
                  <span>{billingData?.billing_city_name}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>

            <tr>
              <td>Pincode</td>
              <td>
                {billingData?.billing_pincode ? (
                  <span>{billingData?.billing_pincode}</span>
                ) : (
                  <span
                    className="profile-add-span"
                    data-bs-toggle="modal"
                    data-bs-target="#billingform"
                  >
                    + Add
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BillingDetails;
